import { useNavigate, useParams, useLocation, Outlet } from "react-router-dom";
import AppRoutes from "../../constants/routes/AppRoutes";
import { useEffect, useState } from "react";
import {
  Alert,
  AlertDescription,
  Button,
} from "@capacit-com/lara-law-shared-ui";
import { ArrowLeft } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@capacit-com/lara-law-shared-ui";
import WorkspaceHeader from "./partials/WorkspaceHeader";
import { useNewConversationFiltersStore } from "@/stores/new-conversation-filters.store";
import WorkspaceListViewSkeleton from "./WorkspaceListViewSkeleton";
import { useDocumentReferenceStore } from "@/stores/document-reference.store";
import PageContent from "../PageContent";
import { flattenDocs } from "@/models/workspace/utils";
import { WorkspaceProvider } from "@/pages/workspace/providers/WorkspaceProvider";
import { useWorkspaceContext } from "@/pages/workspace/providers/WorkspaceContext";
import { useWorkspaceReturn } from "@/pages/workspace/hooks/useWorkspaceReturn";

enum WorkspaceTabs {
  FILES = "files",
  COMMENTS = "comments",
  SETTINGS = "settings",
}

// Component that uses the workspace context
const WorkspaceContent = () => {
  const { workspaceId } = useParams();
  const location = useLocation();
  const { reset } = useDocumentReferenceStore();
  const { setWorkspaceId } = useNewConversationFiltersStore();
  const navigate = useNavigate();
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const {
    workspace,
    isUserAdmin,
    isLoading,
    isParentWorkspace,
    isOrphanedInspection,
  } = useWorkspaceContext();

  const workspaceReturn = useWorkspaceReturn(workspaceId);

  // Determine current tab from the route
  const getCurrentTab = (): WorkspaceTabs => {
    const path = location.pathname;
    if (path.includes("/files")) return WorkspaceTabs.FILES;
    if (path.includes("/comments")) return WorkspaceTabs.COMMENTS;
    if (path.includes("/settings")) return WorkspaceTabs.SETTINGS;
    return WorkspaceTabs.FILES;
  };

  const [selectedTab, setSelectedTab] =
    useState<WorkspaceTabs>(getCurrentTab());

  const onChatWithWorkspace = () => {
    setWorkspaceId(workspace?.id || "");
    navigate(AppRoutes.chat);
  };

  // Handle tab navigation
  const handleTabChange = (tab: WorkspaceTabs) => {
    if (!workspace?.id) return;

    switch (tab) {
      case WorkspaceTabs.FILES:
        navigate(AppRoutes.workspaceFiles(workspace.id));
        break;
      case WorkspaceTabs.COMMENTS:
        navigate(AppRoutes.workspaceComments(workspace.id));
        break;
      case WorkspaceTabs.SETTINGS:
        navigate(AppRoutes.workspaceSettings(workspace.id));
        break;
      default:
        navigate(AppRoutes.workspaceFiles(workspace.id));
        break;
    }
  };
  useEffect(() => {
    if (
      workspace?.id &&
      location.pathname === AppRoutes.workspace(workspace.id)
    ) {
      navigate(AppRoutes.workspaceFiles(workspace.id), {
        replace: true,
      });
    }
  }, [workspace?.id, location.pathname, navigate]);

  useEffect(() => {
    if (!workspace?.id || !isOrphanedInspection) return;

    const onRestrictedTab =
      location.pathname.includes("/comments") ||
      location.pathname.includes("/settings");

    if (onRestrictedTab) {
      navigate(AppRoutes.workspaceFiles(workspace.id), { replace: true });
    }
  }, [workspace?.id, isOrphanedInspection, location.pathname, navigate]);

  useEffect(() => {
    if (workspace) {
      reset();
    }
  }, [workspace, reset]);

  // Update selected tab when route changes
  useEffect(() => {
    setSelectedTab(getCurrentTab());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const renderComments = () => {
    if (isLoading) return;
    if (isParentWorkspace || isOrphanedInspection) return;
    return (
      <TabsTrigger
        value={WorkspaceTabs.COMMENTS}
        onClick={() => handleTabChange(WorkspaceTabs.COMMENTS)}
        className="tab-trigger"
      >
        Kommentarer
      </TabsTrigger>
    );
  };
  const RenderSettings = () => {
    if (isLoading) return;
    if (isParentWorkspace || isOrphanedInspection) return;
    if (!isUserAdmin()) return;
    return (
      <TabsTrigger
        value={WorkspaceTabs.SETTINGS}
        onClick={() => handleTabChange(WorkspaceTabs.SETTINGS)}
        className="tab-trigger"
      >
        Indstillinger
      </TabsTrigger>
    );
  };

  return (
    <PageContent
      title={workspace?.name || "Workspace"}
      parent={{
        title: "Dine workspaces",
        url: AppRoutes.workspaces,
      }}
    >
      {workspace && !isLoading ? (
        <div className={`flex flex-col grow gap-3 p-4 xl:px-8 relative w-full`}>
          {workspaceReturn && (
            <Button
              variant="ghost"
              size="sm"
              className="w-fit -ml-2"
              onClick={() => navigate(workspaceReturn.returnTo)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {workspaceReturn.label}
            </Button>
          )}
          {isOrphanedInspection && (
            <Alert>
              <AlertDescription>
                Forældreløst workspace — skrivebeskyttet visning til vurdering
                før tildeling af ejer.
              </AlertDescription>
            </Alert>
          )}
          <WorkspaceHeader
            callToAction={
              !isOrphanedInspection ? (
                <div>
                  <Button
                    onClick={onChatWithWorkspace}
                    disabled={flattenDocs(workspace).length === 0}
                  >
                    Chat med Workspace
                  </Button>
                </div>
              ) : null
            }
          />

          <Tabs
            value={selectedTab}
            className={`workspace-tabs border-b ${
              isDeleteLoading ? "pointer-events-none opacity-30" : ""
            }`}
          >
            <TabsList className="tabs-list">
              <TabsTrigger
                value={WorkspaceTabs.FILES}
                onClick={() => handleTabChange(WorkspaceTabs.FILES)}
                className="tab-trigger"
              >
                Filer
              </TabsTrigger>
              {renderComments()}
              {RenderSettings()}
            </TabsList>
          </Tabs>

          <Outlet context={{ isDeleteLoading, setIsDeleteLoading }} />
        </div>
      ) : (
        <div className="p-4">
          <WorkspaceListViewSkeleton />
        </div>
      )}
    </PageContent>
  );
};

const WorkspaceView = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!workspaceId) {
      navigate(AppRoutes.workspaceNotFound, {
        replace: true,
      });
      return;
    }
  }, [workspaceId, navigate]);

  if (!workspaceId) {
    return null;
  }

  return (
    <WorkspaceProvider workspaceId={workspaceId}>
      <WorkspaceContent />
    </WorkspaceProvider>
  );
};

export default WorkspaceView;
